/**
 * Legacy URL redirects (Wix → Emergent migration).
 *
 * This module runs BEFORE React Router matches. If the current pathname
 * matches a known legacy URL, we perform a same-origin JS redirect via
 * `window.location.replace(...)`. Google's crawler treats these as an
 * implicit 301: the referring URL drops out of the index and equity flows
 * to the destination. See:
 *   https://developers.google.com/search/docs/crawling-indexing/301-redirects
 *
 * Notes on scope:
 *   • Only URLs where we are confident the destination is the correct
 *     equivalent page are listed. Anything else falls through to the SPA
 *     router which renders <NotFoundPage> with `noindex,nofollow`.
 *   • The map keys are lowercase, path-only (no query, no hash).
 *     Comparison is case-insensitive so /Home and /HOME are covered too.
 *   • Do NOT map arbitrary paths to the homepage — that inflates duplicate
 *     content signals. Prefer the closest topical match; if there is none,
 *     let the URL 404.
 */

// Legacy path (lowercase, no trailing slash) → canonical path
const LEGACY_REDIRECTS = {
  // Locale-prefixed Wix routes → root
  '/az': '/',
  '/en': '/',
  '/ru': '/',
  '/az/home': '/',
  '/en/home': '/',
  '/ru/home': '/',

  // Home aliases
  '/home': '/',
  '/main': '/',
  '/index': '/',
  '/index.html': '/',
  '/index.php': '/',
  '/site': '/',

  // "About" family → biz-kimik
  '/about': '/haqqimizda/biz-kimik',
  '/about-us': '/haqqimizda/biz-kimik',
  '/about-1': '/haqqimizda/biz-kimik',
  '/haqqinda': '/haqqimizda/biz-kimik',
  '/haqqimizda': '/haqqimizda/biz-kimik',
  '/az/haqqimizda': '/haqqimizda/biz-kimik',
  '/en/about': '/haqqimizda/biz-kimik',
  '/en/about-us': '/haqqimizda/biz-kimik',

  // Team / leadership → rehberlik
  '/team': '/haqqimizda/rehberlik',
  '/team-1': '/haqqimizda/rehberlik',
  '/rehberlik': '/haqqimizda/rehberlik',
  '/leadership': '/haqqimizda/rehberlik',

  // Financial reports
  '/hesabat': '/haqqimizda/hesabatlar',
  '/hesabatlar': '/haqqimizda/hesabatlar',
  '/reports': '/haqqimizda/hesabatlar',
  '/financial-reports': '/haqqimizda/hesabatlar',

  // Contact
  '/contact': '/elaqe',
  '/contact-us': '/elaqe',
  '/contact-1': '/elaqe',
  '/eleqe': '/elaqe',
  '/elaqe-1': '/elaqe',
  '/en/contact': '/elaqe',
  '/az/elaqe': '/elaqe',

  // Products family
  '/istehlak': '/mehsullar/istehlak-kreditleri',
  '/istehlak-krediti': '/mehsullar/istehlak-kreditleri',
  '/istehlak-kreditleri': '/mehsullar/istehlak-kreditleri',
  '/consumer-loan': '/mehsullar/istehlak-kreditleri',

  '/qizil': '/mehsullar/qizil-kreditleri',
  '/qizil-krediti': '/mehsullar/qizil-kreditleri',
  '/qizil-kreditleri': '/mehsullar/qizil-kreditleri',
  '/gold-loan': '/mehsullar/qizil-kreditleri',

  '/lizinq': '/mehsullar/avto-lizinq',
  '/avto-lizinq': '/mehsullar/avto-lizinq',
  '/avto-kredit': '/mehsullar/avto-lizinq',
  '/auto-lease': '/mehsullar/avto-lizinq',
  '/car-leasing': '/mehsullar/avto-lizinq',

  // Calculator typos
  '/kalkulyator': '/kalkulator',
  '/calculator': '/kalkulator',
  '/kredit-kalkulyatoru': '/kalkulator',
  '/kredit-kalkulator': '/kalkulator',

  // FAQ
  '/faqs': '/faq',
  '/questions': '/faq',
  '/suallar': '/faq',
  '/tez-tez-verilen-suallar': '/faq',
};

// All real, indexable public routes (lowercase). Used to auto-canonicalise
// casing or trailing-slash variants so /FAQ or /faq/ redirect to /faq.
const CANONICAL_ROUTES = new Set([
  '/',
  '/mehsullar/istehlak-kreditleri',
  '/mehsullar/qizil-kreditleri',
  '/mehsullar/avto-lizinq',
  '/kalkulator',
  '/haqqimizda/biz-kimik',
  '/haqqimizda/rehberlik',
  '/haqqimizda/hesabatlar',
  '/elaqe',
  '/faq',
  '/mexfilik-siyaseti',
  '/istifade-shertleri',
]);

/**
 * Resolve a legacy path to its canonical destination.
 * @returns the new pathname, or null when the URL is not a known legacy URL.
 */
export function resolveLegacyPath(pathname) {
  if (!pathname) return null;
  // Normalise: strip trailing slash (except root), lowercase for map lookup.
  const cleaned = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  const key = cleaned.toLowerCase();

  // Explicit legacy → canonical map.
  if (LEGACY_REDIRECTS[key]) return LEGACY_REDIRECTS[key];

  // Casing / trailing-slash normaliser for real routes.
  // e.g. "/FAQ" → "/faq", "/mehsullar/istehlak-kreditleri/" → same
  if (CANONICAL_ROUTES.has(key) && key !== pathname) return key;

  return null;
}

/**
 * Runs synchronously at app boot. If the current URL is a legacy one, we
 * `window.location.replace()` so the browser (and Google) treats it as a
 * navigation to the canonical URL instead of a soft-404. Preserves any
 * query string and hash so campaign URLs keep their attribution.
 */
export function applyLegacyRedirect() {
  if (typeof window === 'undefined') return false;
  const dest = resolveLegacyPath(window.location.pathname);
  if (!dest || dest === window.location.pathname) return false;
  const url = dest + window.location.search + window.location.hash;
  window.location.replace(url);
  return true;
}
